import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';

export const Route = createFileRoute('/admin/suporte/$userId')({ component: AdminSupportTicket });

type Msg = { id: string; content: string; fromAdmin: boolean; createdAt: string };

type TicketResp = {
  ticket: { id: string; status: string; hasUnreadByAdmin: boolean };
  user: { id: string; name: string | null; email: string; role: string; avatar: string | null };
  messages: Msg[];
};

function AdminSupportTicket() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-support-ticket', userId],
    queryFn: () => api.get(`admin/support/tickets/${userId}`).json<TicketResp>(),
    refetchInterval: 15000,
  });

  const reply = useMutation({
    mutationFn: (content: string) =>
      api.post(`admin/support/tickets/${userId}/messages`, { json: { content } }).json<any>(),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['admin-support-ticket', userId] });
      qc.invalidateQueries({ queryKey: ['admin-support-tickets'] });
    },
  });

  const markRead = useMutation({
    mutationFn: () => api.post(`admin/support/tickets/${userId}/read`).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-support-tickets'] }),
  });

  // Marca como lidas ao abrir (uma vez)
  useEffect(() => {
    if (data?.ticket?.hasUnreadByAdmin) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ticket?.hasUnreadByAdmin]);

  // Auto-scroll
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [data?.messages?.length]);

  function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const txt = draft.trim();
    if (!txt) return;
    reply.mutate(txt);
  }

  if (isLoading || !data) {
    return (
      <>
        <AdminPageHeader title="Suporte" />
        <div className="bg-white rounded-3xl p-12 animate-pulse h-64 border border-slate-100 mt-4" />
      </>
    );
  }

  const { user, messages } = data;

  return (
    <>
      <Link to="/admin/suporte" className="text-sm text-coral-500 hover:underline">
        ← Voltar
      </Link>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm mt-3 flex flex-col" style={{ height: 'calc(100vh - 160px)' }}>
        {/* Header */}
        <header className="px-5 py-3 border-b border-slate-100 flex items-center gap-4">
          {user.avatar ? (
            <img src={user.avatar} alt="" className="size-12 rounded-full object-cover ring-2 ring-teal-100" />
          ) : (
            <div className="size-12 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold">
              {(user.name ?? user.email)?.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-900">{user.name ?? '(sem nome)'}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                {user.role}
              </span>
            </div>
            <div className="text-xs text-slate-500">{user.email}</div>
          </div>
        </header>

        {/* Lista de mensagens */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
          {messages.length === 0 ? (
            <div className="text-center text-sm text-slate-500 mt-8">
              Nenhuma mensagem ainda. Diga olá.
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`flex ${m.fromAdmin ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                    m.fromAdmin
                      ? 'bg-teal-500 text-white rounded-br-sm'
                      : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                  <div className={`text-[10px] mt-1 ${m.fromAdmin ? 'text-teal-100' : 'text-slate-400'}`}>
                    {new Date(m.createdAt).toLocaleString('pt-BR', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="border-t border-slate-100 p-4 flex items-end gap-2 bg-white">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e as any);
              }
            }}
            rows={1}
            placeholder="Digite sua resposta..."
            disabled={reply.isPending}
            className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none max-h-32"
          />
          <button
            type="submit"
            disabled={reply.isPending || !draft.trim()}
            className="bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl text-sm font-medium transition"
          >
            {reply.isPending ? 'Enviando...' : 'Enviar'}
          </button>
        </form>
      </div>
    </>
  );
}
